import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { getEmployees, createEmployee, updateEmployee, deleteEmployee } from "@/lib/api";
import { toast } from "sonner";
import { Plus, MoreHorizontal, Pencil, Trash2, Search, Home, Building2 } from "lucide-react";

export default function Employees() {
  const { t } = useTranslation();
  const { canEdit, canDelete } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    id_card_number: "",
    department: "",
    is_homeoffice: false
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const response = await getEmployees();
      setEmployees(response.data);
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: "",
      last_name: "",
      email: "",
      id_card_number: "",
      department: "",
      is_homeoffice: false
    });
    setEditingItem(null);
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        first_name: item.first_name,
        last_name: item.last_name,
        email: item.email,
        id_card_number: item.id_card_number || "",
        department: item.department,
        is_homeoffice: item.is_homeoffice
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await updateEmployee(editingItem.id, formData);
        toast.success(t('employees.updated'));
      } else {
        await createEmployee(formData);
        toast.success(t('employees.added'));
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('employees.confirmDelete'))) return;
    try {
      await deleteEmployee(id);
      toast.success(t('employees.deleted'));
      loadData();
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const filteredEmployees = employees.filter((item) => {
    const fullName = `${item.first_name} ${item.last_name}`.toLowerCase();
    return (
      fullName.includes(searchTerm.toLowerCase()) ||
      item.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.employee_number.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  return (
    <div data-testid="employees-page">
      <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">{t('employees.title')}</h1>
          <p className="page-subtitle">{t('employees.subtitle')}</p>
        </div>
        {canEdit() && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                onClick={() => handleOpenDialog()} 
                className="btn-primary gap-2"
                data-testid="add-employee-btn"
              >
                <Plus className="w-4 h-4" />
                {t('employees.add')}
              </Button>
            </DialogTrigger>
          <DialogContent className="sm:max-w-lg" data-testid="employee-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                {editingItem ? t('employees.edit') : t('employees.new')}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="form-label">{t('employees.firstName')}</Label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    placeholder="Max"
                    required
                    data-testid="employee-firstname-input"
                  />
                </div>
                <div>
                  <Label className="form-label">{t('employees.lastName')}</Label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    placeholder="Mustermann"
                    required
                    data-testid="employee-lastname-input"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="form-label">{t('employees.email')}</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="max.mustermann@firma.de"
                    required
                    data-testid="employee-email-input"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="form-label">Personalausweisnummer (CIN)</Label>
                  <Input
                    value={formData.id_card_number}
                    onChange={(e) => setFormData({ ...formData, id_card_number: e.target.value })}
                    placeholder="z.B. 12345678"
                    data-testid="employee-idcard-input"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="form-label">{t('employees.department')}</Label>
                  <Input
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    placeholder="z.B. Call Center Team A"
                    required
                    data-testid="employee-department-input"
                  />
                </div>
                <div className="col-span-2 flex items-center gap-3">
                  <Checkbox
                    id="homeoffice"
                    checked={formData.is_homeoffice}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_homeoffice: checked })}
                    data-testid="employee-homeoffice-checkbox"
                  />
                  <Label htmlFor="homeoffice" className="cursor-pointer">
                    {t('employees.worksHomeoffice')}
                  </Label>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                >
                  {t('common.cancel')}
                </Button>
                <Button type="submit" className="btn-primary" data-testid="employee-submit-btn">
                  {editingItem ? t('common.save') : t('common.add')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder={t('employees.search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="employee-search"
          />
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/50">
              <TableHead className="font-semibold">{t('employees.employeeNumber')}</TableHead>
              <TableHead className="font-semibold">{t('employees.firstName')} / {t('employees.lastName')}</TableHead>
              <TableHead className="font-semibold">{t('employees.email')}</TableHead>
              <TableHead className="font-semibold">{t('employees.department')}</TableHead>
              <TableHead className="font-semibold">{t('employees.workplace')}</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                  {t('common.loading')}
                </TableCell>
              </TableRow>
            ) : filteredEmployees.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                  {t('employees.noEntries')}
                </TableCell>
              </TableRow>
            ) : (
              filteredEmployees.map((item) => (
                <TableRow key={item.id} className="table-row-hover" data-testid={`employee-row-${item.id}`}>
                  <TableCell className="font-mono text-sm text-slate-600">
                    {item.employee_number}
                  </TableCell>
                  <TableCell className="font-medium">
                    {item.first_name} {item.last_name}
                  </TableCell>
                  <TableCell className="text-slate-600">{item.email}</TableCell>
                  <TableCell>{item.department}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {item.is_homeoffice ? (
                        <>
                          <Home className="w-4 h-4 text-blue-500" />
                          <span className="text-sm">{t('employees.homeoffice')}</span>
                        </>
                      ) : (
                        <>
                          <Building2 className="w-4 h-4 text-slate-400" />
                          <span className="text-sm">{t('employees.office')}</span>
                        </>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {canEdit() && (
                          <DropdownMenuItem onClick={() => handleOpenDialog(item)}>
                            <Pencil className="w-4 h-4 mr-2" />
                            {t('common.edit')}
                          </DropdownMenuItem>
                        )}
                        {canDelete() && (
                          <DropdownMenuItem 
                            onClick={() => handleDelete(item.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            {t('common.delete')}
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
